import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type {
  CreateTicketInput,
  ListTicketsInput,
  Ticket,
  TicketCategory,
  TicketPriority,
  TicketStatus,
  UpdateTicketInput,
} from '@cp3/shared';

export type { TicketCategory, TicketPriority, TicketStatus };

export interface TicketRow extends Omit<Ticket, 'createdAt' | 'updatedAt' | 'dueAt' | 'deferUntil' | 'resolvedAt' | 'closedAt' | 'lastEscalatedAt' | 'lastEmailedAt'> {
  createdAt?: { _seconds?: number; seconds?: number; toDate?: () => Date } | null;
  updatedAt?: { _seconds?: number; seconds?: number; toDate?: () => Date } | null;
  dueAt?: { _seconds?: number; seconds?: number; toDate?: () => Date } | null;
  deferUntil?: { _seconds?: number; seconds?: number; toDate?: () => Date } | null;
  resolvedAt?: { _seconds?: number; seconds?: number; toDate?: () => Date } | null;
  closedAt?: { _seconds?: number; seconds?: number; toDate?: () => Date } | null;
  lastEscalatedAt?: { _seconds?: number; seconds?: number; toDate?: () => Date } | null;
  lastEmailedAt?: { _seconds?: number; seconds?: number; toDate?: () => Date } | null;
}

export interface TicketCommentRow {
  id: string;
  authorUid: string;
  authorLabel: string;
  body: string;
  emailedToCustomer?: boolean;
  createdAt?: { _seconds?: number; seconds?: number; toDate?: () => Date } | null;
}

const QK_LIST = (filters: ListTicketsInput) => ['tickets', 'list', filters] as const;
const QK_COMMENTS = (id: string) => ['tickets', 'comments', id] as const;

export function tsToDate(v: unknown): Date | null {
  if (!v) return null;
  const obj = v as { toDate?: () => Date; _seconds?: number; seconds?: number };
  if (obj.toDate) return obj.toDate();
  const seconds = obj._seconds ?? obj.seconds;
  if (typeof seconds === 'number') return new Date(seconds * 1000);
  if (typeof v === 'string') return new Date(v);
  return null;
}

export function useTickets(filters: ListTicketsInput = {}) {
  return useQuery<TicketRow[]>({
    queryKey: QK_LIST(filters),
    staleTime: 10_000,
    queryFn: async () => {
      const fn = httpsCallable<ListTicketsInput, { items: TicketRow[] }>(functions, 'listTickets');
      const r = await fn(filters);
      return r.data.items;
    },
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTicketInput) => {
      const fn = httpsCallable<CreateTicketInput, { ok: true; ticketId: string; publicCode: string }>(
        functions,
        'createTicket',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}

export function useUpdateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateTicketInput) => {
      const fn = httpsCallable<UpdateTicketInput, { ok: true }>(functions, 'updateTicket');
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}

export function useEscalateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ticketId: string) => {
      const fn = httpsCallable<{ ticketId: string }, { ok: true }>(functions, 'escalateTicket');
      const r = await fn({ ticketId });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tickets'] }),
  });
}

export function useTicketComments(ticketId: string | null) {
  return useQuery<TicketCommentRow[]>({
    queryKey: QK_COMMENTS(ticketId ?? ''),
    enabled: !!ticketId,
    staleTime: 5_000,
    queryFn: async () => {
      const fn = httpsCallable<{ ticketId: string }, { items: TicketCommentRow[] }>(
        functions,
        'listTicketComments',
      );
      const r = await fn({ ticketId: ticketId! });
      return r.data.items;
    },
  });
}

export function useAddTicketComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ticketId: string; body: string; sendToCustomer?: boolean }) => {
      const fn = httpsCallable<typeof input, { ok: true; commentId: string }>(
        functions,
        'addTicketComment',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: QK_COMMENTS(vars.ticketId) });
      qc.invalidateQueries({ queryKey: ['tickets', 'list'] });
    },
  });
}
